import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Texas A&M University-Corpus Christi (Ward Island campus)
const TAMUCC = [27.7108, -97.3244];
const RADIUS_MILES = 60;
const METERS_PER_MILE = 1609.344;

const CITIES = [
  { name: "Corpus Christi", coords: [27.8006, -97.3964] },
  { name: "Kingsville", coords: [27.5159, -97.8561] },
  { name: "Sinton", coords: [28.0356, -97.5064] },
  { name: "Rockport", coords: [28.0206, -97.0547] },
  { name: "Alice", coords: [27.7523, -98.0698] },
];

const cityIcon = (name) =>
  L.divIcon({
    className: "cp-city",
    html: `<div style="display:flex;align-items:center;gap:6px;">
      <div style="width:10px;height:10px;background:#B22234;border:2px solid #000;border-radius:9999px;box-shadow:0 0 0 1px #fff;"></div>
      <div style="background:#fff;border:1.5px solid #000;color:#0A0A0A;font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:0.15em;text-transform:uppercase;padding:3px 6px;white-space:nowrap;font-weight:600;">${name}</div>
    </div>`,
    iconSize: [140, 20],
    iconAnchor: [5, 10],
  });

const highlightIcon = (name) =>
  L.divIcon({
    className: "cp-highlight",
    html: `<div style="display:flex;align-items:center;gap:8px;">
      <div class="cp-pulse"></div>
      <div style="background:#B22234;border:2px solid #000;box-shadow:3px 3px 0 #000;color:#fff;font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:0.15em;text-transform:uppercase;padding:4px 8px;white-space:nowrap;font-weight:700;">${name}</div>
    </div>`,
    iconSize: [200, 24],
    iconAnchor: [9, 12],
  });

const fullBounds = () => L.latLng(TAMUCC).toBounds(RADIUS_MILES * METERS_PER_MILE * 2);

export default function ServiceAreaMap({ selected, onReset }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const highlightRef = useRef(null);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;

    const map = L.map(containerRef.current, {
      center: TAMUCC,
      zoom: 8,
      scrollWheelZoom: false,
      zoomControl: true,
      attributionControl: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>',
    }).addTo(map);

    L.circle(TAMUCC, {
      radius: RADIUS_MILES * METERS_PER_MILE,
      color: "#B22234",
      weight: 3,
      opacity: 1,
      fillColor: "#B22234",
      fillOpacity: 0.12,
    }).addTo(map);

    CITIES.forEach((c) => L.marker(c.coords, { icon: cityIcon(c.name) }).addTo(map));

    map.fitBounds(fullBounds(), { padding: [10, 10] });
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    highlightRef.current?.remove();
    highlightRef.current = null;
    if (!selected) {
      map.flyToBounds(fullBounds(), { padding: [10, 10], duration: 0.8 });
      return;
    }
    highlightRef.current = L.marker(selected.coords, {
      icon: highlightIcon(selected.name),
      zIndexOffset: 1000,
    }).addTo(map);
    map.flyTo(selected.coords, 11, { duration: 1.2 });
  }, [selected]);

  return (
    <div
      data-testid="service-area-map"
      className="relative border-2 border-black bg-white hard-shadow-lg"
    >
      <div ref={containerRef} className="w-full h-[420px]" style={{ zIndex: 1 }} />
      <div className="absolute top-3 left-14 bg-white border-2 border-black px-3 py-2 font-mono text-[10px] uppercase tracking-widest z-[500] pointer-events-none">
        ↳ 60-mile service radius
      </div>
      {selected && (
        <button
          type="button"
          onClick={onReset}
          data-testid="map-reset-btn"
          className="absolute top-3 right-3 bg-black text-white border-2 border-black px-3 py-2 font-mono text-[10px] uppercase tracking-widest z-[500] hover:bg-[#B22234] transition-colors"
        >
          Show full area
        </button>
      )}
      <div className="absolute bottom-3 left-3 bg-[#B22234] text-white border-2 border-black px-3 py-2 font-mono text-[10px] uppercase tracking-widest z-[500] pointer-events-none hard-shadow-sm">
        Anchored @ TAMU-CC
      </div>
    </div>
  );
}
